import type { ComponentType } from 'react';
import SkipToLink from 'components/accessibility/SkipToLink';
import { BasketPowerstepQuickViewAtom } from 'components/basket/BasketPowerstep/shared/hooks/useBasketPowerstepQuickView';
import { ShippingOptionsQuickViewAtom } from 'components/basket/DeliveryPreview/shared/hooks/useShippingOptionsQuickView';
import { ConfirmationQuickViewState } from 'components/ConfirmationQuickView/shared/hooks';
import CookieConsentLoader from 'components/CookieConsent/CookieConsentLoader';
import { SignUpNudgeQuickViewState } from 'components/clubMatas/SignUpNudgeQuickView/shared/hooks/useSignUpNudgeQuickView';
import useExpectedDeliveryQuickView, {
    ExpectedDeliveryQuickViewState
} from 'components/expecteddelivery/shared/hooks/useExpectedDeliveryQuickView';
import { FindationQuickViewAtom } from 'components/Findation/hooks/useFindationQuickView';
import DrawerRenderer from 'components/generic/Drawer/DrawerRenderer';
import { priceLabelInfoState } from 'components/generic/PriceModule/components/PriceLabelRenderer/hooks/usePriceLabelInfo';
import { LoginInfoQuickViewState } from 'components/login/shared/hooks/useLoginInfoQuickView';
import { ChangeSubscriptionTypeAtom } from 'components/matasPlus/ChangeSubscriptionNudge/shared/hooks/useChangeSubscriptionType';
import { PlusOfferNudgeActivatedQuickViewAtom } from 'components/matasPlus/PlusOfferNudge/components/PlusOfferNudgeActivatedQuickView/hooks/usePlusOfferNudgeActivatedQuickView';
import { PlusOfferNudgeQuickViewAtom } from 'components/matasPlus/PlusOfferNudge/components/PlusOfferNudgeQuickView/hooks/usePlusOfferNudgeQuickView';
import { PlusSignUpQuickViewAtom } from 'components/matasPlus/PlusOfferNudge/components/PlusSignUpQuickView/hooks/usePlusSignUpQuickView';
import { MatasPlusDetailsQuickViewAtom } from 'components/matasPlus/savingsBanner/components/DetailsQuickView/hooks/useMatasPlusDetailsQuickView';
import { MatasPlusDisclaimerQuickViewAtom } from 'components/matasPlus/savingsBanner/components/DisclaimerQuickView/hooks/useMatasPlusDisclaimerQuickView';
import { ArticleUniverseFilterQuickViewAtom } from 'components/pages/ArticleUniverse/components/Filter/QuickView/useArticleUniverseFilterQuickView';
import { PowerstepMediatorAtom } from 'components/powerstep/shared/hooks/usePowerstep';
import { CertificationDrawerAtom } from 'components/product/certification/useCertificationDrawer';
import { NotificationFormQuickViewState } from 'components/product/notification/shared/hooks/useNotificationFormQuickView';
import { RevieveVTOQuickViewAtom } from 'components/product/RevieveVirtualTryOn/hooks/useRevieveVTOQuickView';
import { RegimeQuickViewState } from 'components/product/regime/components/RegimeQuickView/components/RegimeQuickViewButton/hooks/useRegimeQuickView';
import { VariantPickerQuickViewState } from 'components/product/Variants/VariantPicker/shared/hooks/useVariantPickerQuickView';
import { SponsoredSplashQuickViewAtom } from 'components/shared/Splash/components/SponsoredSplash/hooks/useSponsoredSplashQuickView';
import TooltipPortal from 'components/shared/Tooltip/components/TooltipPortal';
import {
    FindInStoreQuickViewMediatorAtom,
    FindInStoreWithStoreStockQuickViewMediatorAtom
} from 'components/storeList/shared/hooks';
import { SurveyQuickViewAtom } from 'components/surveySystem/shared/hooks/useOpenSurveyQuickView';
import { vimeoQuickViewMediatorAtom } from 'components/video/hooks/useVimeoQuickView';
import dynamic from 'helpers/dynamic';
import { type MediatorAtom, useMediatorValue } from 'hooks/globals/useMediatorAtom';

const DefaultToastContainer = dynamic(() => import('components/Toast/DefaultToastContainer'), { ssr: false });
const PowerstepQuickview = dynamic(() => import('components/powerstep/components/PowerstepQuickview'), { ssr: false });
const SignUpNudgeQuickView = dynamic(() => import('components/clubMatas/SignUpNudgeQuickView'), { ssr: false });
const LoginInfoQuickView = dynamic(() => import('components/login/LoginInfoQuickview'), { ssr: false });
const CertificationQuickView = dynamic(() => import('components/product/certification/CertificationDrawer'), {
    ssr: false
});
const ExpectedDeliveryQuickView = dynamic(
    () => import('components/expecteddelivery/components/ExpectedDeliveryQuickView'),
    { ssr: false }
);
const ChangeSubscriptionQuickView = dynamic(
    () => import('components/matasPlus/ChangeSubscriptionNudge/components/ChangeSubscriptionQuickView'),
    { ssr: false }
);
const TerminatePending = dynamic(() => import('components/matasPlus/TerminatePending/TerminatePending'), {
    ssr: false
});
const PriceLabelInfoQuickView = dynamic(
    () => import('components/generic/PriceModule/components/PriceLabelRenderer/components/PriceLabelInfoQuickView'),
    { ssr: false }
);
const NotificationFormQuickView = dynamic(
    () => import('components/product/notification/components/NotificationFormQuickView'),
    { ssr: false }
);
const VariantPickerQuickView = dynamic(
    () => import('components/product/Variants/VariantPicker/VariantPickerQuickView'),
    { ssr: false }
);
const FindInStoreQuickView = dynamic(() => import('components/storeList/components/FindInStoreQuickView'), {
    ssr: false
});
const FindInStoreWithStoreStockQuickView = dynamic(
    () => import('components/storeList/components/FindInStoreWithStoreStockQuickView'),
    {
        ssr: false
    }
);
const ConfirmationQuickView = dynamic(() => import('components/ConfirmationQuickView'), {
    ssr: false
});
const BasketPowerstepQuickview = dynamic(() => import('components/basket/BasketPowerstep/BasketPowerstepQuickView'), {
    ssr: false
});
const VimeoQuickView = dynamic(() => import('components/video/components/VimeoQuickView'), {
    ssr: false
});

const ShippingOptionsQuickView = dynamic(() => import('components/basket/DeliveryPreview/ShippingOptionsQuickView'), {
    ssr: false
});

const SponsoredSplashQuickView = dynamic(
    () => import('components/shared/Splash/components/SponsoredSplash/SponsoredSplashQuickView'),
    { ssr: false }
);

const MatasPlusDetailsQuickView = dynamic(
    () => import('components/matasPlus/savingsBanner/components/DetailsQuickView/DetailsQuickView'),
    { ssr: false }
);

const MatasPlusDisclaimerQuickView = dynamic(
    () => import('components/matasPlus/savingsBanner/components/DisclaimerQuickView'),
    { ssr: false }
);

const RegimeQuickView = dynamic(() => import('components/product/regime/components/RegimeQuickView'), {
    ssr: false
});

const FindationQuickView = dynamic(() => import('components/Findation'), {
    ssr: false
});

const RevieveVTOQuickView = dynamic(() => import('components/product/RevieveVirtualTryOn'), {
    ssr: false
});

const SurveyQuickView = dynamic(() => import('components/surveySystem/SurveyQuickView'), {
    ssr: false
});

const ArticleUniverseFilterQuickView = dynamic(
    () => import('components/pages/ArticleUniverse/components/Filter/QuickView/QuickView'),
    {
        ssr: false
    }
);

const PlusOfferNudgeQuickView = dynamic(
    () => import('components/matasPlus/PlusOfferNudge/components/PlusOfferNudgeQuickView'),
    { ssr: false }
);

const PlusOfferNudgeActivatedQuickView = dynamic(
    () => import('components/matasPlus/PlusOfferNudge/components/PlusOfferNudgeActivatedQuickView'),
    { ssr: false }
);

const PlusSignUpQuickView = dynamic(
    () => import('components/matasPlus/PlusOfferNudge/components/PlusSignUpQuickView'),
    { ssr: false }
);

const loadedGlobalComponents: string[] = [];

const GlobalQuickViewLoader = <T extends { isOpen: boolean }>({
    Component,
    name,
    mediatorAtom
}: {
    Component: ComponentType<Record<string, unknown>>;
    name: string;
    mediatorAtom: MediatorAtom<T>;
}) => {
    const { isOpen } = useMediatorValue(mediatorAtom);
    const hasLoadedComponent = loadedGlobalComponents.includes(name);
    const open = isOpen || hasLoadedComponent;

    if (isOpen && !hasLoadedComponent) {
        loadedGlobalComponents.push(name);
    }

    if (open) {
        return <Component />;
    }

    return null;
};

function SpawnQuickViewFromJSHooks() {
    const [, toggleExpectedDelivery] = useExpectedDeliveryQuickView();

    if (typeof window === 'undefined') {
        return null;
    }

    document.addEventListener('click', (event) => {
        const { target } = event;

        if (!(target instanceof Element)) {
            return;
        }

        if (target.closest('[data-js-quickView]')) {
            const value = target.getAttribute('data-js-quickView');

            switch (value) {
                case 'expextedDelivery':
                    toggleExpectedDelivery({ isOpen: true, params: { type: 'basket' } });
                    break;
                default:
                    break;
            }
        }
    });

    return null;
}

export default function GlobalComponents() {
    return (
        <>
            {/* Global Components - should be placed at top-level when we have single-react app */}

            <SkipToLink />

            <DefaultToastContainer />
            <TerminatePending />
            <SpawnQuickViewFromJSHooks />
            <TooltipPortal />
            <DrawerRenderer />

            <CookieConsentLoader />

            <GlobalQuickViewLoader
                Component={PowerstepQuickview}
                name="PowerstepQuickview"
                mediatorAtom={PowerstepMediatorAtom}
            />
            <GlobalQuickViewLoader
                Component={SignUpNudgeQuickView}
                name="SignUpNudgeQuickView"
                mediatorAtom={SignUpNudgeQuickViewState}
            />
            <GlobalQuickViewLoader
                Component={LoginInfoQuickView}
                name="LoginInfoQuickView"
                mediatorAtom={LoginInfoQuickViewState}
            />
            <GlobalQuickViewLoader
                Component={CertificationQuickView}
                name="CertificationQuickView"
                mediatorAtom={CertificationDrawerAtom}
            />
            <GlobalQuickViewLoader
                Component={ExpectedDeliveryQuickView}
                name="ExpectedDeliveryQuickView"
                mediatorAtom={ExpectedDeliveryQuickViewState}
            />
            <GlobalQuickViewLoader
                Component={ChangeSubscriptionQuickView}
                name="ChangeSubscriptionQuickView"
                mediatorAtom={ChangeSubscriptionTypeAtom}
            />
            <GlobalQuickViewLoader
                Component={PriceLabelInfoQuickView}
                name="PriceLabelInfoQuickView"
                mediatorAtom={priceLabelInfoState}
            />
            <GlobalQuickViewLoader
                Component={NotificationFormQuickView}
                name="NotificationFormQuickView"
                mediatorAtom={NotificationFormQuickViewState}
            />
            <GlobalQuickViewLoader
                Component={VariantPickerQuickView}
                name="VariantPickerQuickView"
                mediatorAtom={VariantPickerQuickViewState}
            />
            <GlobalQuickViewLoader
                Component={FindInStoreQuickView}
                name="FindInStoreQuickView"
                mediatorAtom={FindInStoreQuickViewMediatorAtom}
            />
            <GlobalQuickViewLoader
                Component={FindInStoreWithStoreStockQuickView}
                name="FindInStoreWithStoreStockQuickView"
                mediatorAtom={FindInStoreWithStoreStockQuickViewMediatorAtom}
            />
            <GlobalQuickViewLoader
                Component={ConfirmationQuickView}
                name="ConfirmationQuickView"
                mediatorAtom={ConfirmationQuickViewState}
            />
            <GlobalQuickViewLoader
                Component={BasketPowerstepQuickview}
                name="BasketPowerstepQuickView"
                mediatorAtom={BasketPowerstepQuickViewAtom}
            />
            <GlobalQuickViewLoader
                Component={ShippingOptionsQuickView}
                name="ShippingOptionsQuickView"
                mediatorAtom={ShippingOptionsQuickViewAtom}
            />
            <GlobalQuickViewLoader
                Component={VimeoQuickView}
                name="VimeoQuickView"
                mediatorAtom={vimeoQuickViewMediatorAtom}
            />
            <GlobalQuickViewLoader
                Component={SponsoredSplashQuickView}
                name="SponsoredSplashQuickView"
                mediatorAtom={SponsoredSplashQuickViewAtom}
            />
            <GlobalQuickViewLoader
                Component={MatasPlusDetailsQuickView}
                name="MatasPlusDetailsQuickView"
                mediatorAtom={MatasPlusDetailsQuickViewAtom}
            />
            <GlobalQuickViewLoader
                Component={MatasPlusDisclaimerQuickView}
                name="MatasPlusDisclaimerQuickView"
                mediatorAtom={MatasPlusDisclaimerQuickViewAtom}
            />
            <GlobalQuickViewLoader
                Component={RegimeQuickView}
                name="RegimeQuickView"
                mediatorAtom={RegimeQuickViewState}
            />
            <GlobalQuickViewLoader
                Component={FindationQuickView}
                name="FindationQuickView"
                mediatorAtom={FindationQuickViewAtom}
            />
            <GlobalQuickViewLoader
                Component={RevieveVTOQuickView}
                name="RevieveVTOQuickView"
                mediatorAtom={RevieveVTOQuickViewAtom}
            />
            <GlobalQuickViewLoader
                Component={SurveyQuickView}
                name="SurveyQuickView"
                mediatorAtom={SurveyQuickViewAtom}
            />
            <GlobalQuickViewLoader
                Component={ArticleUniverseFilterQuickView}
                name="ArticleUniverseFilterQuickView"
                mediatorAtom={ArticleUniverseFilterQuickViewAtom}
            />
            <GlobalQuickViewLoader
                Component={PlusOfferNudgeQuickView}
                name="PlusOfferNudgeQuickView"
                mediatorAtom={PlusOfferNudgeQuickViewAtom}
            />
            <GlobalQuickViewLoader
                Component={PlusOfferNudgeActivatedQuickView}
                name="PlusOfferNudgeActivatedQuickView"
                mediatorAtom={PlusOfferNudgeActivatedQuickViewAtom}
            />
            <GlobalQuickViewLoader
                Component={PlusSignUpQuickView}
                name="PlusSignUpQuickView"
                mediatorAtom={PlusSignUpQuickViewAtom}
            />
        </>
    );
}
